import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  ChevronLeft, 
  Save, 
  Loader2,
  MapPin,
  Euro,
  Image as ImageIcon,
  Car,
  Trash2,
  Shield
} from "lucide-react";

import { API } from "../utils/api";

const FEATURES_OPTIONS = [
  { id: "covered", label: "Cubierto" },
  { id: "security", label: "Vigilancia 24h" },
  { id: "ev_charging", label: "Carga eléctrica" },
  { id: "lighting", label: "Iluminado" },
  { id: "easy_access", label: "Fácil acceso" },
  { id: "large_vehicle", label: "Vehículo grande" },
  { id: "motorcycle", label: "Moto" },
];

export default function EditSpot() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    address: "",
    city: "",
    country: "España",
    price_hour: "",
    price_day: "",
    price_month: "",
    features: [],
    images: [""],
    latitude: "",
    longitude: "",
    has_plazafy_gate: false
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
      return;
    }
    if (id && user) {
      loadSpot();
    }
  }, [id, user, authLoading, navigate]);

  const loadSpot = async () => {
    try {
      const response = await axios.get(`${API}/spots/${id}`);
      const spot = response.data;
      
      // Verificar que el usuario es el propietario
      if (spot.owner_id !== user.id) {
        toast.error("No tienes permiso para editar esta plaza");
        navigate("/my-spots");
        return;
      }
      
      setFormData({
        title: spot.title || "",
        description: spot.description || "",
        address: spot.address || "",
        city: spot.city || "",
        country: spot.country || "España",
        price_hour: spot.price_hour?.toString() || "",
        price_day: spot.price_day?.toString() || "",
        price_month: spot.price_month?.toString() || "",
        features: spot.features || [],
        images: spot.images?.length > 0 ? spot.images : [""],
        latitude: spot.latitude?.toString() || "",
        longitude: spot.longitude?.toString() || "",
        has_plazafy_gate: spot.has_plazafy_gate || false
      });
    } catch (error) {
      toast.error("Error al cargar la plaza");
      navigate("/my-spots");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFeatureToggle = (featureId) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(featureId)
        ? prev.features.filter(f => f !== featureId)
        : [...prev.features, featureId]
    }));
  };

  const handleImageChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const addImageField = () => {
    setFormData(prev => ({ ...prev, images: [...prev.images, ""] }));
  };

  const removeImageField = (index) => {
    if (formData.images.length > 1) {
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const submitData = {
        ...formData,
        price_hour: parseFloat(formData.price_hour) || 0,
        price_day: parseFloat(formData.price_day) || 0,
        price_month: parseFloat(formData.price_month) || 0,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        images: formData.images.filter(img => img.trim() !== "")
      };

      await axios.put(`${API}/spots/${id}`, submitData);
      toast.success("Plaza actualizada correctamente");
      navigate("/my-spots");
    } catch (error) {
      const message = error.response?.data?.detail || "Error al actualizar la plaza";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Estás seguro de que quieres eliminar esta plaza? Esta acción no se puede deshacer.")) {
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/spots/${id}`);
      toast.success("Plaza eliminada correctamente");
      navigate("/my-spots");
    } catch (error) {
      const message = error.response?.data?.detail || "Error al eliminar la plaza";
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen" data-testid="edit-spot-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/my-spots")}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Volver a Mis Plazas
          </button>
          
          <Button
            variant="ghost"
            onClick={handleDelete}
            disabled={deleting}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
          >
            {deleting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            Eliminar plaza
          </Button>
        </div>

        <div className="dashboard-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-violet-500/20 rounded-lg">
              <Car className="w-6 h-6 text-violet-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Editar Plaza</h1>
              <p className="text-gray-400">Actualiza la información de tu plaza de garaje</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Información básica */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <MapPin className="w-5 h-5 text-violet-400" />
                Información básica
              </h2>
              
              <div>
                <Label className="text-gray-300">Título</Label>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Ej: Plaza de garaje céntrica"
                  required
                  className="bg-[#15121e] border-white/10 text-white"
                />
              </div>

              <div>
                <Label className="text-gray-300">Descripción</Label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe tu plaza de garaje..."
                  rows={4}
                  className="bg-[#15121e] border-white/10 text-white"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Dirección</Label>
                  <Input
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Calle, número..."
                    required
                    className="bg-[#15121e] border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Ciudad</Label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Ej: Madrid"
                    required
                    className="bg-[#15121e] border-white/10 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Precios */}
            <div className="space-y-4 pt-4 border-t border-white/10">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Euro className="w-5 h-5 text-violet-400" />
                Precios
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-300">Precio/hora (€)</Label>
                  <Input
                    name="price_hour"
                    type="number"
                    step="0.01"
                    value={formData.price_hour}
                    onChange={handleChange}
                    placeholder="2.50"
                    className="bg-[#15121e] border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Precio/día (€)</Label>
                  <Input
                    name="price_day"
                    type="number"
                    step="0.01"
                    value={formData.price_day}
                    onChange={handleChange}
                    placeholder="15.00"
                    className="bg-[#15121e] border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Precio/mes (€)</Label>
                  <Input
                    name="price_month"
                    type="number"
                    step="0.01"
                    value={formData.price_month}
                    onChange={handleChange}
                    placeholder="80.00"
                    className="bg-[#15121e] border-white/10 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Características */}
            <div className="space-y-4 pt-4 border-t border-white/10">
              <h2 className="text-lg font-semibold text-white">Características</h2>
              <div className="flex flex-wrap gap-2">
                {FEATURES_OPTIONS.map((feature) => (
                  <button
                    key={feature.id}
                    type="button"
                    onClick={() => handleFeatureToggle(feature.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      formData.features.includes(feature.id)
                        ? "bg-violet-500 text-white"
                        : "bg-white/5 text-gray-400 hover:bg-white/10"
                    }`}
                  >
                    {feature.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Plazafy Gate */}
            <div className="space-y-4 pt-4 border-t border-white/10">
              <div className="bg-gradient-to-r from-violet-500/10 to-emerald-500/10 rounded-xl p-5 border border-violet-500/20">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-violet-400" />
                  Plazafy Gate
                  <span className="ml-2 px-2 py-0.5 bg-violet-500/20 text-violet-400 text-xs rounded-full">
                    Nuevo
                  </span>
                </h2>
                
                <div className="flex items-center justify-between p-4 bg-[#15121e] rounded-lg border border-white/10">
                  <div>
                    <p className="text-white font-medium">¿Tienes Plazafy Gate instalado?</p>
                    <p className="text-gray-400 text-sm">
                      Marca esta opción si tu plaza tiene el dispositivo de control de acceso inteligente.
                      <a href="/plazafy-gate" target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:text-violet-300 ml-1">
                        Más info →
                      </a>
                    </p>
                  </div>
                  <Switch
                    checked={formData.has_plazafy_gate}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, has_plazafy_gate: checked }))}
                    data-testid="edit-plazafy-gate-switch"
                  />
                </div>
              </div>
            </div>

            {/* Imágenes */}
            <div className="space-y-4 pt-4 border-t border-white/10">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-violet-400" />
                Imágenes
              </h2>
              
              {formData.images.map((image, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={image}
                    onChange={(e) => handleImageChange(index, e.target.value)}
                    placeholder="URL de la imagen"
                    className="bg-[#15121e] border-white/10 text-white flex-1"
                  />
                  {formData.images.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => removeImageField(index)}
                      className="text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              
              <Button
                type="button"
                variant="outline"
                onClick={addImageField}
                className="border-white/10 text-gray-300 hover:bg-white/5"
              >
                + Añadir otra imagen
              </Button>
            </div>

            {/* Submit */}
            <div className="pt-6 border-t border-white/10">
              <Button
                type="submit"
                className="w-full h-12 bg-violet-500 hover:bg-violet-600 text-white"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    Guardar cambios
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
